import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseFullscreenReturn {
  fullscreen: boolean;
  fullscreenElement: HTMLElement | null;
  setFullscreenElement: (element: HTMLElement | null) => void;
  openFullscreen: () => Promise<void | undefined>;
  closeFullscreen: () => Promise<void>;
}

export const useFullscreen = (): UseFullscreenReturn => {
  const fullscreenElement = useRef<HTMLElement | null>(null);
  const unsubscribeCallback = useRef<() => void>();
  const [fullscreen, setFullscreen] = useState(false);

  const unsubscribe = useCallback(() => {
    unsubscribeCallback.current?.();
    unsubscribeCallback.current = undefined;
  }, []);

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  const setFullscreenElement = useCallback(
    (element: HTMLElement | null) => {
      if (element === fullscreenElement.current) return;

      unsubscribe();

      fullscreenElement.current = element;

      if (!element) {
        setFullscreen(false);
        return false;
      }

      const toggleFullscreen = (event: Event): void => {
        if (event.target !== element) return;

        setFullscreen(prev => !prev);
      };

      element.addEventListener('fullscreenchange', toggleFullscreen);

      unsubscribeCallback.current = () => {
        element.removeEventListener('fullscreenchange', toggleFullscreen);
      };
    },
    [unsubscribe],
  );

  const openFullscreen = useCallback(async (): Promise<void | undefined> => {
    if (!fullscreenElement.current) return;

    return fullscreenElement.current.requestFullscreen();
  }, []);

  const closeFullscreen = useCallback(async (): Promise<void> => {
    return document.exitFullscreen();
  }, []);

  return {
    fullscreenElement: fullscreenElement.current,
    fullscreen,
    setFullscreenElement,
    openFullscreen,
    closeFullscreen,
  };
};
